import {analyzeMatrixRank} from '../../../numeric/rankRevealingQr';
import {SketchParametricJacobian} from './SketchParametricJacobian';
import {SketchParametricLeastSquaresSolver} from './SketchParametricLeastSquaresSolver';
import type {
    SketchParametricCandidateResult,
    SketchParametricEquation,
    SketchParametricPointHandle,
    SketchParametricRankResult,
    SketchParametricSolveResult,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SketchParametricParameterVector} from './SketchParametricParameterVector';
import {SKETCH_PARAMETRIC_TOLERANCE} from './SketchParametricToleranceConfig';
import {SketchParametricResidualEvaluator} from './SketchParametricResidualEvaluator';

/**
 * 草图参数化求解系统门面。
 *
 * 设计说明（语义蓝本：阶段 0 原型 `SketchPrototypeSystem`）：
 * 1. 一个系统对应一个 solver island：管理参数向量与方程列表；
 * 2. 提供求解、秩/自由度分析入口；
 * 3. 候选方程分类协议（独立/冗余/冲突/无解/退化）保证失败恢复 before；
 * 4. 分支 seed 语义：一切求解从当前参数值出发，编辑值 = 换方程 + 从旧解 seed 重解。
 */
export class SketchParametricSystem {
    /** 参数向量。 */
    private readonly parameters = new SketchParametricParameterVector();

    /** 已提交方程列表。 */
    private readonly equations: SketchParametricEquation[] = [];

    /** 数值雅可比计算器。 */
    private readonly jacobianCalculator = new SketchParametricJacobian();

    /** LM 求解器。 */
    private readonly solver: SketchParametricLeastSquaresSolver;

    /** 冗余容差满足比；1 表示全部残差进入各自量纲冗余容差。 */
    private readonly redundancyTolerance: number;

    /** 统一残差量纲评价器。 */
    private readonly residualEvaluator = new SketchParametricResidualEvaluator();

    /**
     * @param options 系统选项。
     */
    public constructor(options?: {
        readonly solver?: SketchParametricLeastSquaresSolver;
        readonly redundancyTolerance?: number;
    }) {
        this.solver = options?.solver ?? new SketchParametricLeastSquaresSolver();
        this.redundancyTolerance = options?.redundancyTolerance ?? SKETCH_PARAMETRIC_TOLERANCE.redundancy;
    }

    /**
     * 注册草图点（沿用 island 参数 ID 约定 `point:<pointId>:x|y`）。
     *
     * @param pointId 点稳定 ID。
     * @param x 初始 x（米，草图局部坐标）。
     * @param y 初始 y（米，草图局部坐标）。
     * @param fixed 是否固定。
     * @returns 点参数句柄。
     */
    public addPoint(pointId: string, x: number, y: number, fixed = false): SketchParametricPointHandle {
        const xId = `point:${pointId}:x`;
        const yId = `point:${pointId}:y`;

        this.parameters.addParameter(xId, 'x', x, fixed);
        this.parameters.addParameter(yId, 'y', y, fixed);

        return {xId, yId};
    }

    /**
     * 注册圆/圆弧半径参数。
     *
     * @param entityId 圆/圆弧实体稳定 ID。
     * @param radius 初始半径（米）。
     * @param fixed 是否固定。
     * @returns 半径参数 ID。
     */
    public addRadiusParameter(entityId: string, radius: number, fixed = false): string {
        const radiusId = `entity:${entityId}:radius`;

        this.parameters.addParameter(radiusId, 'radius', radius, fixed);

        return radiusId;
    }

    /**
     * 注册一次属性编辑使用的固定半径目标参数。
     *
     * 该参数只存在于当前参数系统，不进入实体参数映射、关系持久化或历史快照；
     * 它作为严格偏好 seed 的固定来源，确保用户输入半径优先保持。
     *
     * @param intentId 本次属性编辑意图 ID。
     * @param targetRadius 目标半径（米）。
     * @returns 固定目标参数 ID。
     */
    public addFixedRadiusTargetParameter(intentId: string, targetRadius: number): string {
        const parameterId = `intent:${intentId}:radius-target`;

        this.parameters.addParameter(parameterId, 'radius', targetRadius, true);

        return parameterId;
    }

    /**
     * 注册直槽口半宽参数。
     *
     * 说明：槽口的真实自由度是「轴两端 + 半宽」；轴复用已有的中心线起止点参数，
     * 这里只新增半宽这一个标量，配合结构耦合方程把接合点/端帽圆心钉到结构位置上。
     *
     * @param slotId 槽口稳定 ID。
     * @param halfWidth 初始半宽（米）。
     * @param fixed 是否固定。
     * @returns 半宽参数 ID。
     */
    public addSlotHalfWidthParameter(slotId: string, halfWidth: number, fixed = false): string {
        const halfWidthId = `entity:${slotId}:slot-half-width`;

        this.parameters.addParameter(halfWidthId, 'slot-half-width', halfWidth, fixed);

        return halfWidthId;
    }

    /**
     * 注册圆弧槽口结构标量参数（中心弧半径 / 半宽 / 起止角）。
     *
     * 说明：圆弧槽口的自由度是「弧心 + R + h + θs/θe」，其中弧心复用已有的
     * 弧心点参数，这里注册其余标量；角度不做 2π 归一，保持连续可导。
     *
     * @param slotId 槽口稳定 ID。
     * @param role 结构角色（radius/half-width/start-angle/end-angle）。
     * @param value 初始值。
     * @param fixed 是否固定。
     * @returns 参数 ID。
     */
    public addArcSlotStructureParameter(
        slotId: string,
        role: 'radius' | 'half-width' | 'start-angle' | 'end-angle',
        value: number,
        fixed = false,
    ): string {
        const parameterId = `entity:${slotId}:arc-slot-${role}`;
        const parameterRole = role === 'radius'
            ? 'radius'
            : role === 'half-width'
                ? 'slot-half-width'
                : 'scaled-angle';

        this.parameters.addParameter(parameterId, parameterRole, value, fixed);

        return parameterId;
    }

    /**
     * 注册样条整体缩放参数。
     *
     * 说明：样条曲线由 Worker 内的 Puppy CAD 内核插值算出，残差无法同步复算，
     * 因此把可驱动自由度收敛为「锚点平移 + 整体缩放」——缩放 s 倍则弧长恰好 s 倍。
     *
     * @param splineId 样条稳定 ID。
     * @param scale 初始缩放系数（恒为 1）。
     * @param fixed 是否固定。
     * @returns 缩放参数 ID。
     */
    public addSplineScaleParameter(splineId: string, scale: number, fixed = false): string {
        const scaleId = `entity:${splineId}:spline-scale`;

        this.parameters.addParameter(scaleId, 'scale', scale, fixed);

        return scaleId;
    }

    /**
     * 注册公式内部使用的曲线参数。
     *
     * 曲线参数属于相切/点在曲线关系的局部未知量，不对应运行时草图对象，也不进入几何写回。
     *
     * @param ownerId 关系或公式稳定 ID。
     * @param value 初始参数值。
     * @returns 曲线参数 ID。
     */
    public addCurveParameter(ownerId: string, value: number): string {
        const parameterId = `equation:${ownerId}:curve-parameter`;

        this.parameters.addParameter(parameterId, 'curve-parameter', value, false);

        return parameterId;
    }

    /**
     * 注册抛物线可见段端点的长度曲线参数。
     *
     * 与关系内部使用的无量纲 `curve-parameter` 不同，抛物线标准式中的参数表示
     * 顶点局部侧向轴长度，属于实体定义的一部分；固定状态由本次直接操作目标决定。
     *
     * @param entityId 抛物线实体稳定 ID。
     * @param endpointRole 可见段端点角色。
     * @param value 初始侧向长度参数。
     * @param fixed 本轮求解是否固定该参数。
     * @returns 端点曲线参数 ID。
     */
    public addParabolaEndpointParameter(
        entityId: string,
        endpointRole: 'start' | 'end',
        value: number,
        fixed = false,
    ): string {
        const parameterId = `entity:${entityId}:parabola-${endpointRole}-parameter`;

        this.parameters.addParameter(parameterId, 'curve-length-parameter', value, fixed);

        return parameterId;
    }

    /**
     * 判断参数是否已注册。
     *
     * @param parameterId 参数 ID。
     * @returns 是否已注册。
     */
    public hasParameter(parameterId: string): boolean {
        return this.parameters.has(parameterId);
    }

    /**
     * 读取参数当前值。
     *
     * @param parameterId 参数 ID。
     * @returns 当前值。
     */
    public read(parameterId: string): number {
        return this.parameters.read(parameterId);
    }

    /**
     * 获取参数向量（构建器/写回适配器使用）。
     *
     * @returns 参数向量。
     */
    public getParameterVector(): SketchParametricParameterVector {
        return this.parameters;
    }

    /**
     * 校验当前解的参数定义域。
     *
     * @throws 当最终解包含非有限值、负半径、负槽宽或负缩放时抛出错误。
     */
    public assertParameterDomain(): void {
        this.parameters.assertCurrentValuesInDomain();
    }

    /**
     * 获取已提交方程列表（只读视图）。
     *
     * @returns 方程列表。
     */
    public getEquations(): readonly SketchParametricEquation[] {
        return this.equations;
    }

    /**
     * 直接提交方程（不做候选分类；构建既有系统或已确认方程时使用）。
     *
     * @param equation 残差方程。
     * @throws 当相同 ID 的方程已存在时抛出错误。
     */
    public addEquation(equation: SketchParametricEquation): void {
        if (this.equations.some((existing) => existing.equationId === equation.equationId)) {
            throw new Error(`方程已存在：${equation.equationId}`);
        }
        this.equations.push(equation);
    }

    /**
     * 按 ID 移除方程（编辑尺寸值 = 替换方程后从旧解 seed 重解）。
     *
     * @param equationId 方程 ID。
     * @throws 当指定 ID 的方程不存在时抛出错误。
     */
    public removeEquation(equationId: string): void {
        const index = this.equations.findIndex((equation) => equation.equationId === equationId);

        if (index < 0) {
            throw new Error(`方程不存在：${equationId}`);
        }
        this.equations.splice(index, 1);
    }

    /**
     * 从当前 seed 求解全部方程。
     *
     * @param options 本次求解的取消选项。
     * @returns 求解结果。
     */
    public solve(options?: {readonly shouldCancel?: () => boolean}): SketchParametricSolveResult {
        const beforeSnapshot = this.parameters.snapshotValues();
        let result: SketchParametricSolveResult;

        try {
            result = this.solver.solve(this.parameters, this.equations, options?.shouldCancel);
        } catch (error) {
            /** 数值求值异常也必须恢复稳定 seed，禁止把半次迭代留在参数系统中。 */
            this.parameters.restoreValues(beforeSnapshot);
            throw error;
        }

        if (!result.converged) {
            /** 取消、奇异或达到迭代上限都不是可提交解，统一恢复本次求解前状态。 */
            this.parameters.restoreValues(beforeSnapshot);
            return result;
        }

        try {
            /** 只有同时满足残差容差与参数定义域，才是可提交的参数解。 */
            this.assertParameterDomain();
        } catch (error) {
            this.parameters.restoreValues(beforeSnapshot);
            throw error;
        }

        return result;
    }

    /**
     * 对当前方程组做秩/自由度分析。
     *
     * @returns 秩分析结果。
     */
    public analyzeRank(): SketchParametricRankResult {
        const freeParameterIds = this.parameters.getFreeParameterIds();
        const hardEquations = this.equations.filter((equation) => equation.strength !== 'preference');
        const jacobian = this.jacobianCalculator.compute(
            this.parameters,
            hardEquations,
            freeParameterIds,
        );

        return analyzeMatrixRank(jacobian, freeParameterIds.length);
    }

    /**
     * 捕获参数值快照（before 采集/分支 seed）。
     *
     * @returns 参数值快照。
     */
    public snapshotValues(): Record<string, number> {
        return this.parameters.snapshotValues();
    }

    /**
     * 恢复参数值快照（失败回滚/undo 语义）。
     *
     * @param snapshot 参数值快照。
     */
    public restoreValues(snapshot: Record<string, number>): void {
        this.parameters.restoreValues(snapshot);
    }

    /**
     * 分类并尝试提交候选方程。
     *
     * 提交协议（对齐 02 计划 §7.4 与历史规范“失败恢复 before”）：
     * - 独立且收敛：提交方程与新几何，返回 fully/under-defined；
     * - 独立但不收敛（unsolvable）：恢复 before 参数并移除方程；
     * - 冗余/冲突/invalid：不修改系统，只报告，由上层（过定义选项）决定
     *   转 driven、保留或放弃；solver 不弹 UI。
     *
     * @param candidate 候选残差方程。
     * @returns 分类结果。
     */
    public classifyAndCommitCandidate(candidate: SketchParametricEquation): SketchParametricCandidateResult {
        const freeParameterIds = this.parameters.getFreeParameterIds();
        const hardEquations = this.equations.filter((equation) => equation.strength !== 'preference');

        let rankBefore: number;
        let rankAfter: number;

        try {
            const baseJacobian = this.jacobianCalculator.compute(
                this.parameters,
                hardEquations,
                freeParameterIds,
            );

            rankBefore = analyzeMatrixRank(baseJacobian, freeParameterIds.length).rank;

            const augmentedJacobian = this.jacobianCalculator.compute(
                this.parameters,
                [...hardEquations, candidate],
                freeParameterIds,
            );

            rankAfter = analyzeMatrixRank(augmentedJacobian, freeParameterIds.length).rank;
        } catch (error) {
            // 退化几何：方程无法求值，归为 invalid，不污染系统。
            return {
                classification: 'invalid',
                solveResult: null,
                rankBefore: 0,
                rankAfter: 0,
                degreesOfFreedomAfter: null,
                message: error instanceof Error ? error.message : String(error),
            };
        }

        if (rankAfter <= rankBefore) {
            // 不增秩：依赖方程，按当前解的一致性区分冗余与冲突。
            const maxCandidateResidual = this.residualEvaluator.calculateMaxRedundancyRatio(
                candidate,
                (parameterId) => this.parameters.read(parameterId),
            );
            const redundant = maxCandidateResidual <= this.redundancyTolerance;

            return {
                classification: redundant ? 'redundant' : 'conflicting',
                solveResult: null,
                rankBefore,
                rankAfter,
                degreesOfFreedomAfter: null,
                message: redundant
                    ? '候选方程被现有方程组蕴含，且与当前解一致'
                    : `候选方程与现有解不一致，最大冗余容差比 ${maxCandidateResidual}`,
            };
        }

        // 独立方程：捕获 before，试解；失败恢复 before。
        const beforeSnapshot = this.parameters.snapshotValues();

        this.equations.push(candidate);
        let solveResult: SketchParametricSolveResult;

        try {
            solveResult = this.solver.solve(this.parameters, this.equations);
        } catch (error) {
            this.parameters.restoreValues(beforeSnapshot);
            this.equations.pop();

            return {
                classification: 'invalid',
                solveResult: null,
                rankBefore,
                rankAfter,
                degreesOfFreedomAfter: null,
                message: error instanceof Error ? error.message : String(error),
            };
        }

        if (!solveResult.converged) {
            this.parameters.restoreValues(beforeSnapshot);
            this.equations.pop();

            return {
                classification: 'unsolvable',
                solveResult,
                rankBefore,
                rankAfter,
                degreesOfFreedomAfter: null,
                message: `独立方程在当前分支下无解，最大容差满足比 ${solveResult.maxResidual}`,
            };
        }

        try {
            /** 候选只有在最终参数仍位于合法定义域时才可提交。 */
            this.assertParameterDomain();
        } catch (error) {
            this.parameters.restoreValues(beforeSnapshot);
            this.equations.pop();

            return {
                classification: 'invalid',
                solveResult,
                rankBefore,
                rankAfter,
                degreesOfFreedomAfter: null,
                message: error instanceof Error ? error.message : String(error),
            };
        }

        const degreesOfFreedomAfter = freeParameterIds.length - rankAfter;

        return {
            classification: degreesOfFreedomAfter === 0
                ? 'independent-fully-defined'
                : 'independent-under-defined',
            solveResult,
            rankBefore,
            rankAfter,
            degreesOfFreedomAfter,
        };
    }
}
